﻿import 'components/settings/settings.css';
import { useValidation } from "hooks/useValidation";
import { FormCheckbox, FormHelperText } from "components/form-fields";
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useState } from 'react';
import { EmployeeService } from "services";
import { ListingTable } from "components/listing";
import _ from 'underscore';
import { Modal } from 'components/notifications';
import { DocumentUploader, ViewDocumentModal } from 'pages/policy/shared';
import { Button } from 'components/buttons';
import { Intent } from '@blueprintjs/core';
import { Tooltip } from 'components/tooltip';
import { PolicyDocumentTypes } from 'pages/policy/document/constants/PolicyDocumentTypes';


export function PolicyDocumentSettings(
    {
        disabled = false,
        isDataDirty = false,
        documents,
        loading,
        onSettingsUpdate,
        onValidationUpdate
    }
) {

    const noDataMessage = "You have no documents, please upload a document.";
    const tableHeaders = ["", "Name", "Type", "Version", ""];
    const [tableRows, setTableRows] = useState([]);
    const [isValid, errors, validate] = useValidation();
    const [loadingValue, setLoading] = useState(true);
    const [documentSettings, setDocumentSettings] = useState(
        {
            policyDocuments: []
        });
    const [documentsValue, setDocuments] = useState([]);
    const [allDocuments, setAllDocuments] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);

    const [uploadDocumentModalOpen, setUploadDocumentModalOpen] = useState(false);
    const [documentToPreview, setDocumentToPreview] = useState(null);

    useEffect(initialLoad, []);

    function initialLoad() {
        loadDocuments();
    }

    function loadDocuments() {
        setLoading(true);
        EmployeeService.getPolicyDocuments().then(rdata => {
            setAllDocuments(rdata);
            setLoading(false);
        });
    }

    useEffect(onValidationChange, [isValid])
    function onValidationChange() {
        onValidationUpdate(isValid);
    }

    useEffect(onDocumentChange, [documentsValue])
    function onDocumentChange() {
        let data = [];

        if (documentsValue != null) {
            setTotalRecords(documentsValue.length);
            data = documentsValue.map((d) => {

                return [(<FormCheckbox
                    checked={d.checked}
                    label=''
                    key={'documentList'}
                    id={d.id}
                    onChange={handleToggleChange}
                    loading={loadingValue}
                    disabled={d.disabled || disabled}
                    dangerHelperText={errors.policyDocuments}
                />),
                d.name,
                d.type,
                d.version,
                <Tooltip key={`view-doc-tootip-${d.documentId}-${d.dueFromDate}`} content="View document" position="top">
                    <Button key={`view-doc-button-${d.documentId}-${d.dueFromDate}`} minimal={true} large={false} onClick={() => (previewDocument(d))} intent="primary" icon="eye-open" />
                </Tooltip>,
                ];
            });
        }

        setTableRows(data);
    }

    useEffect(() => {
        setDocumentSettings(documents);
        setDocuments(filterLatestOrSelectedDocuments(mapDocuments(allDocuments, documents.policyDocuments)));
    }, [documents, allDocuments]);

    useEffect(() => {
        setLoading(loading);
    }, [loading]);

    useEffect(updateValidationRules, [documents])

    function previewDocument(document) {
        setDocumentToPreview(document);
    }

    function checkMultipleVersions() {
        var multipleVersionsSelected = [];
        var countByName = _.countBy(documents.policyDocuments, function (item) {
            return item.name;
        });

        for (const name in countByName) {
            if (countByName[name] > 1) {
                multipleVersionsSelected.push(name);
            }
        }
        return multipleVersionsSelected;
    }

    function updateValidationRules() {
        let rules = [];

        rules.push({ fieldName: "policyDocuments", required: true });

        var multipleVersionsSelected = checkMultipleVersions();

        if (multipleVersionsSelected.length > 0) {
            var externalRule = {
                fieldName: "policyDocuments",
                valid: false,
                errorMessage: 'Only one version of the same document can be selected. You have selected multiple versions of ' + multipleVersionsSelected.join(', ')
            };
            var externalRules = [externalRule];
        }

        validate(rules, documents, externalRules);
    }

    function filterLatestOrSelectedDocuments(data) {
        return _.filter(data, function (d) {
            return d.isLatestVersion || d.checked;
        });
    }

    function mapDocuments(data, policyDocuments) {
        return _.map(data, function (item) {
            const isSelected = _.some(policyDocuments, function (document) {
                return document.id === item.id;
            });
            return { id: item.id, name: item.name, documentStorageIdentifier: item.documentStorageIdentifier, checked: isSelected, disabled: false, type: item.type, version: item.versionNumber, isLatestVersion: item.isLatestVersion };
        });
    }

    function handleToggleChange(event) {
        var temp = [...documentSettings.policyDocuments];
        var id = parseInt(event.currentTarget.getAttribute('identifier'));
        if (event.target.checked) {
            var selectedDocument = _.find(allDocuments, function (item) {
                return item.id === id;
            });
            selectedDocument.checked = true;
            temp.push(selectedDocument);
        } else {
            temp = temp.filter(function (e) {
                return e.id !== id
            });
        }

        var updatedSettings = {
            ...documentSettings,
            policyDocuments: temp
        }
        setDocumentSettings(updatedSettings);
        onSettingsUpdate(updatedSettings);
    }

    function onFinishUploading() {
        loadDocuments();
        setUploadDocumentModalOpen(false);
    }

    return (
        <Fragment>
            <FormHelperText loading={loadingValue}>
                <p>Here you can select the documents you want your employees to read via this policy.</p>
            </FormHelperText>
            <div className="button-row spacer-bottom">
                <Tooltip content={isDataDirty ? "You cannot upload documents when new documents have been selected." : ""}>
                    <Button
                        id='upload-document-button'
                        onClick={() => setUploadDocumentModalOpen(true)}
                        loading={loadingValue}
                        intent={Intent.PRIMARY}
                        disabled={disabled || isDataDirty}
                        text={'Upload Documents'}
                    />
                </Tooltip>
            </div>
            <ListingTable
                id="listing-table-document"
                headers={tableHeaders}
                data={tableRows}
                loadingData={loadingValue}
                noDataMessage={noDataMessage}
                pageable={false}
                totalRecordCount={totalRecords}
                disabled={disabled}
            />
            <div className="form-field">
                <FormHelperText danger={true}>{errors.policyDocuments}</FormHelperText>
            </div>
            <Modal
                isOpen={uploadDocumentModalOpen}
                title="Upload Documents"
                onClose={() => setUploadDocumentModalOpen(false)}
                size="large"
                clickOutsideClose={false}
            >
                <DocumentUploader
                    onFinish={onFinishUploading}
                    hideGenericOption={true}
                    onCancel={() => setUploadDocumentModalOpen(false)}
                    inModal={true}
                />
            </Modal>
            <ViewDocumentModal
                isOpen={documentToPreview != null}
                onClose={() => setDocumentToPreview(null)}
                documentData={{
                    documentName: documentToPreview?.name,
                    documentStorageIdentifier: documentToPreview?.documentStorageIdentifier,
                    documentType: PolicyDocumentTypes.UNSIGNED,
                }}
            />
        </Fragment>
    );
}

PolicyDocumentSettings.propTypes = {
    documents: PropTypes.object.isRequired,
    disabled: PropTypes.bool,
    loading: PropTypes.bool.isRequired,
    onSettingsUpdate: PropTypes.func.isRequired,
    onValidationUpdate: PropTypes.func.isRequired,
    isDataDirty: PropTypes.bool
};